/**
 * Consent-gated Google tag (gtag.js) loader.
 *
 * This is the single place that injects gtag.js and translates the cookie-banner
 * choice into Consent Mode v2 signals. It used to exist as two near-identical
 * copies inside CookieConsent.tsx (one in the provider for the page-reload path,
 * one in the banner for the click path), which meant neither path was unit
 * testable and a fix applied to one silently missed the other.
 *
 * See cursor/docs/incidents/2026-07-31-ga4-csp-blocks-collect.md
 */

export interface ConsentSettings {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

export interface TrackingIds {
  ga4Id?: string;
  googleAdsId?: string;
}

export const GTAG_SCRIPT_ID = "gtag-base";
export const GA4_READY_EVENT = "ga4Ready";
export const CONSENT_STORAGE_KEY = "cookieConsent";

/**
 * `SameSite=Lax` — NOT `None`.
 *
 * `SameSite=None` marks `_ga` as a cross-site cookie, which Safari ITP and
 * Firefox ETP reject outright and Chrome's third-party-cookie phase-out caps.
 * This is a first-party GA4 install, so `Lax` is both correct and the only
 * value that survives those browsers.
 */
const COOKIE_FLAGS = "SameSite=Lax;Secure";

type GtagLike = (...args: unknown[]) => void;

function getGtag(): GtagLike | undefined {
  if (typeof window === "undefined") return undefined;
  const gtag = (window as unknown as { gtag?: GtagLike }).gtag;
  return typeof gtag === "function" ? gtag : undefined;
}

/** Measurement IDs the given consent choice permits us to configure. */
export function resolveTagIds(
  settings: ConsentSettings,
  tracking: TrackingIds
): string[] {
  const ids: string[] = [];
  if (settings.analytics && tracking.ga4Id) ids.push(tracking.ga4Id);
  if (settings.marketing && tracking.googleAdsId) ids.push(tracking.googleAdsId);
  return ids;
}

/** Push the Consent Mode v2 `update` that mirrors the user's choice. */
export function applyConsentMode(settings: ConsentSettings): void {
  const gtag = getGtag();
  if (!gtag) return;

  gtag("consent", "update", {
    analytics_storage: settings.analytics ? "granted" : "denied",
    ad_storage: settings.marketing ? "granted" : "denied",
    ad_user_data: settings.marketing ? "granted" : "denied",
    ad_personalization: settings.marketing ? "granted" : "denied",
  });
}

/**
 * Inject gtag.js (once) and configure every consented measurement ID.
 *
 * The Consent Mode v2 *defaults* are already pushed by the `consent-mode-v2`
 * inline script in the root layout, which runs `beforeInteractive` — i.e. before
 * this ever executes. We only push the `update` here, so denied-by-default is
 * never skipped even if this module never runs.
 *
 * @returns `true` when at least one tag was configured.
 */
export function loadGoogleTags(
  settings: ConsentSettings,
  tracking: TrackingIds
): boolean {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return false;
  }

  const ids = resolveTagIds(settings, tracking);

  // Nothing consented: make the denial explicit and stop. No script injected.
  if (ids.length === 0) {
    applyConsentMode(settings);
    return false;
  }

  if (!document.getElementById(GTAG_SCRIPT_ID)) {
    const script = document.createElement("script");
    script.id = GTAG_SCRIPT_ID;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${ids[0]}`;
    script.async = true;
    document.head.appendChild(script);
  }

  const w = window as unknown as { dataLayer?: unknown[]; gtag?: GtagLike };
  w.dataLayer = w.dataLayer || [];
  if (typeof w.gtag !== "function") {
    // Fallback stub for the (unexpected) case where the layout script did not
    // run. Defaults are re-asserted here so consent can never start granted.
    w.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      w.dataLayer!.push(arguments);
    };
    w.gtag("consent", "default", {
      ad_storage: "denied",
      analytics_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
      wait_for_update: 500,
    });
  }

  const gtag = getGtag();
  if (!gtag) return false;

  // `gtag('js', …)` seeds the tag's clock and is pushed once, not per ID.
  gtag("js", new Date());
  for (const id of ids) {
    gtag("config", id, {
      cookie_flags: COOKIE_FLAGS,
      send_page_view: true,
      // Enhanced Conversions ("Erweiterte Conversions für das Web"). Only
      // meaningful on the Ads destination — GA4 ignores it. The user-provided
      // data itself is attached per-conversion, see @utils/enhancedConversions.
      ...(id.startsWith("AW-") ? { allow_enhanced_conversions: true } : {}),
    });
  }

  applyConsentMode(settings);
  return true;
}

/**
 * Whether `loadGoogleTags` has already run its `config` calls.
 *
 * The injected `<script id="gtag-base">` is the marker: it is only added on the
 * path that also pushes `gtag('config', …)`. Anything that fires a conversion
 * on page load has to check this first — `window.gtag` is *always* defined (the
 * root layout installs the stub `beforeInteractive`), so an event sent before
 * `config` silently queues ahead of it in `dataLayer` and is discarded when
 * gtag.js replays the queue in order.
 *
 * See cursor/docs/incidents/2026-07-31-ga4-csp-blocks-collect.md
 */
export function areGoogleTagsLoaded(): boolean {
  if (typeof document === "undefined") return false;
  return document.getElementById(GTAG_SCRIPT_ID) !== null;
}

/** Notify listeners that gtag has been configured and events may be sent. */
export function announceGA4Ready(): void {
  if (typeof window === "undefined" || !window.dispatchEvent) return;
  window.dispatchEvent(new CustomEvent(GA4_READY_EVENT));
}

/** Read the persisted banner choice. Returns null when absent or corrupt. */
export function readStoredConsent(): ConsentSettings | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as ConsentSettings;
  } catch {
    return null;
  }
}
